import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import { Sparkles, Save, Wand2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export const CaptionsEditor = ({ open, onOpenChange, mediaId, onSaved }) => {
  const { user } = useAuth();
  const isPremium = user?.role === "owner" || user?.is_premium;
  const [vtt, setVtt] = useState("");
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open || !mediaId) return;
    api.get(`/media/${mediaId}/captions`).then((r) => setVtt(r.data.vtt || "")).catch(() => {});
  }, [open, mediaId]);

  const generate = async () => {
    if (!isPremium) {
      toast.error("Premium feature — upgrade your squad to auto-transcribe.");
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post(`/media/${mediaId}/transcribe`);
      setVtt(res.data.vtt || "");
      toast.success("Whisper transcription ready · edit before saving");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transcription failed");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/media/${mediaId}/captions`, { vtt });
      toast.success("Captions saved");
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl border-black/10" data-testid="captions-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">Captions · subtitles</DialogTitle>
          <DialogDescription className="text-xs uppercase tracking-[0.2em] text-[#E60000] font-bold">
            Whisper auto-transcribe · edit before publishing · WebVTT
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3">
          <Button
            onClick={generate}
            disabled={generating || !isPremium}
            className="rounded-sm bg-black hover:bg-[#E60000] text-white"
            data-testid="captions-generate-button"
          >
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            {generating ? "Whisper transcribing…" : "Auto-transcribe with Whisper"}
          </Button>
          {!isPremium && (
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E60000] flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Premium-only
            </span>
          )}
        </div>

        <Textarea
          value={vtt}
          onChange={(e) => setVtt(e.target.value)}
          placeholder={`WEBVTT\n\n00:00:00.000 --> 00:00:03.000\nWelcome to the Monster Squad.`}
          className="rounded-sm min-h-[260px] font-mono text-sm"
          data-testid="captions-textarea"
        />

        <div className="flex justify-end gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="captions-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white" data-testid="captions-save">
            <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save captions"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
